import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";
import { DatabaseBackup, Download, Upload, Loader2 } from "lucide-react";

export default function DatabaseTransfer() {
  const { t } = useLang();
  const { refresh } = useSettings();
  const [busy, setBusy] = useState(false);
  const ref = useRef();

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").split("filename=")[1] || "school-backup.json";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t("backup.downloadFailed"));
    } finally {
      setBusy(false);
    }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    if (ref.current) ref.current.value = "";
    if (!file || !window.confirm(t("backup.confirm"))) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/backup/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("backup.restored"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface-card rounded-xl border border-border bg-card p-5 space-y-3" data-testid="db-transfer-card">
      <div className="flex items-center gap-2 font-heading font-semibold text-foreground">
        <DatabaseBackup className="h-5 w-5" style={{ color: "var(--brand)" }} /> {t("backup.title")}
      </div>
      <p className="text-sm text-muted-foreground">{t("backup.help")}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={download} disabled={busy} data-testid="backup-download-btn" className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t("backup.download")}
        </button>
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid="backup-restore-btn" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-60">
          <Upload className="h-4 w-4" /> {t("backup.restore")}
        </button>
        <input ref={ref} type="file" accept=".json" className="hidden" onChange={restore} data-testid="backup-file-input" />
      </div>
    </div>
  );
}
